/**
 * PAINEL — os cinco blocos do protótipo, na ordem dele.
 *
 * A pergunta que a tela responde é "em que pé está a operação e o que precisa
 * de mim", nunca "quanto já fizemos". Por isso o último bloco é a fila de
 * decisão humana, e por isso a capacidade nunca aparece sozinha: capacidade
 * ociosa ao lado de base esgotada é a mentira por omissão mais fácil de contar
 * num painel — o gestor lê folga onde já não há o que disparar.
 */
import { api, type RespostaPainel } from "../api.js";
import { irPara } from "../Casca.js";
import {
  Aviso,
  Botao,
  Carregando,
  Indicador,
  Medidor,
  Painel as Cartao,
  Selo,
  Tabela,
  Vazio,
} from "../ui/base.js";
import { ErroTela, useCarga } from "../ui/carga.js";
import { LinhaVazia, Motivos, Rotulo } from "../ui/dados.js";
import { conta, dataHora, dec, num, plural, relativo } from "../ui/formato.js";
import { passar, secaoDaRota } from "../ui/alvo.js";
import { ESTADO_NUMERO, ORDEM_ESTADO, POOL } from "../ui/rotulos.js";

export function Painel(): JSX.Element {
  const { carga, recarregar } = useCarga(() => api.painel(), "painel");

  if (carga.fase === "carregando") return <Carregando />;
  if (carga.fase === "erro")
    return <ErroTela mensagem={carga.mensagem} aoTentar={recarregar} />;

  const d = carga.dados;

  return (
    <div className="flex flex-col gap-5">
      <Parque parque={d.parque} />
      <Esteira esteira={d.esteira} />
      <Progresso progresso={d.progresso} />
      <Motor motor={d.motor} />
      <Atencao itens={d.atencao} />
    </div>
  );
}

/* ── 1. Em que pé estão os números ────────────────────────────────── */

function Parque({ parque }: { parque: RespostaPainel["parque"] }): JSX.Element {
  const dias = parque.diasDeBaseRestante;
  const base = parque.baseContactavel;

  // Dois avisos distintos porque são dois problemas distintos: sem número apto
  // não há como disparar; sem base não há o que disparar. Fundi-los esconderia
  // qual dos dois precisa ser resolvido primeiro.
  const semApto = parque.aptos === 0;
  const semBase = base === 0 || (dias !== null && dias < 1);

  const notaCapacidade =
    base === 0
      ? "sem base contactável — capacidade ociosa"
      : dias === null
        ? `${num(base)} contatos na base · ritmo ainda sem histórico`
        : `${num(base)} contatos · ${dec(dias)} dia(s) de base neste ritmo`;

  return (
    <div className="flex flex-col gap-4">
      {semApto && (
        <Aviso tom="crit">
          <strong>Nenhum número apto.</strong> Estar cadastrado ou conectado não
          é estar apto: enquanto nenhum número concluir o plano e tiver
          liberação vigente, nenhuma campanha pode sair.
        </Aviso>
      )}

      {semBase && !semApto && (
        <Aviso tom="warn">
          <strong>A base acabou, não a capacidade.</strong>{" "}
          {base === 0
            ? "Não há contato contactável para consumir a capacidade de hoje."
            : `Restam ${num(base)} contatos — menos de um dia no ritmo atual.`}{" "}
          Aumentar número não resolve falta de base.
        </Aviso>
      )}

      <section
        aria-label="Situação dos números"
        className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(190px,1fr))]"
      >
        {ORDEM_ESTADO.map((e) => {
          const info = ESTADO_NUMERO[e];
          const quantos = conta(parque.porEstado, e);
          return (
            <button
              key={e}
              type="button"
              onClick={() => {
                passar("numeros", { estado: e });
                irPara("numeros");
              }}
              className="text-left bg-superficie border border-linha rounded-[10px] px-4 py-3.5 hover:bg-superficie-2 hover:border-linha-forte transition-colors"
            >
              <div className="text-[30px] leading-9 font-semibold tabular tracking-tight">
                {num(quantos)}
              </div>
              <div className="mt-1.5">
                <Selo tom={info.tom}>{info.rotulo}</Selo>
              </div>
              <div className="text-[11.5px] text-tinta-3 mt-1.5 leading-snug">
                {info.nota}
              </div>
            </button>
          );
        })}
      </section>

      <section
        aria-label="Capacidade e base"
        className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(190px,1fr))]"
      >
        <Indicador
          rotulo="Números aptos"
          valor={num(parque.aptos)}
          nota={`de ${plural(parque.total, "número cadastrado", "números cadastrados")}`}
        />
        <Indicador
          rotulo="Capacidade hoje"
          valor={num(parque.capacidadeHoje)}
          nota={notaCapacidade}
        />
        <Indicador
          rotulo="Base contactável"
          valor={num(base)}
          nota={
            dias === null
              ? "sem ritmo apurado para projetar"
              : `dura ${dec(dias)} dia(s) na capacidade de hoje`
          }
        />
        <Indicador
          rotulo="Divisão por pool"
          valor={`${num(conta(parque.porPool, "disparo"))} / ${num(conta(parque.porPool, "sdr"))}`}
          nota={`${POOL.disparo} / ${POOL.sdr}`}
        />
      </section>
    </div>
  );
}

/* ── 2. A esteira ─────────────────────────────────────────────────── */

function Esteira({
  esteira,
}: {
  esteira: RespostaPainel["esteira"];
}): JSX.Element {
  const etapas = [...esteira.etapas].sort((a, b) => a.ordem - b.ordem);

  return (
    <Cartao
      titulo="A esteira"
      dica="Do cadastro à liberação — quantos números param em cada etapa"
      semCorpo
    >
      {etapas.length === 0 ? (
        <Vazio>
          Nenhuma etapa configurada. Comece pelos planos de aquecimento.
        </Vazio>
      ) : (
        <div className="overflow-x-auto">
          <ol className="flex m-0 p-1 list-none min-w-max">
            {etapas.map((e) => (
              <li
                key={`${e.ordem}-${e.nome}`}
                className="min-w-[150px] flex-1 border border-linha border-l-0 first:border-l px-4 py-3.5 bg-superficie"
              >
                <div className="text-[10px] text-tinta-3 tabular">
                  {String(e.ordem).padStart(2, "0")}
                </div>
                <div className="text-[12px] text-tinta-2 mt-0.5 leading-snug">
                  {e.nome}
                </div>
                <div
                  className={`text-[24px] leading-7 font-semibold tabular tracking-tight mt-1.5 ${
                    e.quantidade === 0 ? "text-tinta-3" : "text-tinta"
                  }`}
                >
                  {num(e.quantidade)}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </Cartao>
  );
}

/* ── 3. O desenvolvimento de cada número ──────────────────────────── */

function Progresso({
  progresso,
}: {
  progresso: RespostaPainel["progresso"];
}): JSX.Element {
  // Do mais adiantado ao mais atrasado: quem está perto de liberar é decisão
  // de hoje; quem mal começou não é.
  const ordenados = [...progresso].sort(
    (a, b) => b.progressoPct - a.progressoPct,
  );

  return (
    <Cartao
      titulo="Desenvolvimento de cada número"
      dica="Do mais adiantado ao mais atrasado. Clique no número para abrir a ficha."
      semCorpo
    >
      <Tabela
        larguraMin={860}
        colunas={[
          { chave: "n", rotulo: "Número" },
          { chave: "e", rotulo: "Estado" },
          { chave: "p", rotulo: "Plano e etapa" },
          { chave: "g", rotulo: "Progresso" },
          { chave: "f", rotulo: "O que falta" },
        ]}
      >
        {ordenados.length === 0 ? (
          <LinhaVazia colunas={5}>
            Nenhum número em aquecimento. Cadastre ou importe números para o
            plano começar a rodar.
          </LinhaVazia>
        ) : (
          ordenados.map((n) => {
            const info = ESTADO_NUMERO[n.estado];
            return (
              <tr
                key={n.numeroId}
                className="border-t border-linha hover:bg-superficie-2"
              >
                <td className="px-5 py-3 align-top">
                  <button
                    type="button"
                    onClick={() => {
                      passar("numeros", { numeroId: n.numeroId });
                      irPara("numeros");
                    }}
                    className="text-left"
                  >
                    <span className="block text-[13px] font-medium tabular">
                      {n.telefone}
                    </span>
                    <span className="block text-[11.5px] text-tinta-3">
                      {n.apelido ?? "sem apelido"}
                    </span>
                  </button>
                </td>
                <td className="px-5 py-3 align-top">
                  <Selo tom={info.tom}>{info.rotulo}</Selo>
                </td>
                <td className="px-5 py-3 align-top text-[12.5px]">
                  {n.planoNome ?? "sem plano"}
                  <span className="block text-[11.5px] text-tinta-3">
                    {n.etapaNome
                      ? `Etapa ${n.etapaOrdem ?? "?"} de ${n.etapasTotal ?? "?"} — ${n.etapaNome}`
                      : "sem etapa em curso"}
                  </span>
                </td>
                <td className="px-5 py-3 align-top">
                  <Medidor pct={n.progressoPct} />
                </td>
                <td className="px-5 py-3 align-top min-w-[240px]">
                  <Motivos
                    itens={
                      n.pendencias.length === 0
                        ? [
                            {
                              texto:
                                n.estado === "apto"
                                  ? "Cumpriu o plano e está liberado."
                                  : "Sem pendência registrada.",
                              sinal: "positivo",
                            },
                          ]
                        : n.pendencias.map((p) => ({
                            texto: p,
                            sinal: "negativo" as const,
                          }))
                    }
                  />
                </td>
              </tr>
            );
          })
        )}
      </Tabela>
    </Cartao>
  );
}

/* ── 4. Ritmo do motor ────────────────────────────────────────────── */

function Motor({ motor }: { motor: RespostaPainel["motor"] }): JSX.Element {
  return (
    <Cartao
      titulo="Ritmo do motor"
      dica="O aquecimento não depende de manter esta tela aberta"
      acoes={
        motor.motorLigado ? (
          <Selo tom="ok">Motor ligado</Selo>
        ) : (
          <Selo tom="crit">Motor parado</Selo>
        )
      }
    >
      <div className="flex flex-col gap-4">
        {!motor.motorLigado && (
          <Aviso tom="warn">
            O motor está parado: nada avança de etapa e nenhuma atividade nova é
            programada enquanto ele não for religado.
          </Aviso>
        )}

        <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(170px,1fr))]">
          <Indicador
            rotulo="Atividades hoje"
            valor={num(motor.atividadesHoje)}
            nota="programadas para o dia"
          />
          <Indicador
            rotulo="Concluídas"
            valor={num(motor.concluidasHoje)}
            nota="executadas com sucesso"
          />
          <Indicador
            rotulo="Com falha"
            valor={num(motor.falhasHoje)}
            nota="tentaram e não passaram"
          />
          <Indicador
            rotulo="Bloqueadas por regra"
            valor={num(motor.bloqueadasHoje)}
            nota="a regra segurou antes de sair"
          />
        </div>

        <div className="text-[12.5px] text-tinta-2">
          Próxima atividade:{" "}
          {motor.proximaAtividadeEm ? (
            <>
              <span className="tabular">
                {dataHora(motor.proximaAtividadeEm)}
              </span>{" "}
              <span className="text-tinta-3">
                ({relativo(motor.proximaAtividadeEm)})
              </span>
            </>
          ) : (
            <span className="text-tinta-3">
              nenhuma programada — a fila está vazia
            </span>
          )}
        </div>
      </div>
    </Cartao>
  );
}

/* ── 5. Quem precisa de você ──────────────────────────────────────── */

type Gravidade = RespostaPainel["atencao"][number]["gravidade"];

/** Cor não é veredito: a gravidade precisa estar escrita ao lado dela. */
const GRAVIDADE: Record<Gravidade, string> = {
  crit: "Crítico",
  warn: "Atenção",
  ember: "Em curso",
  ok: "Informativo",
  cool: "Informativo",
  idle: "Informativo",
};

function Atencao({ itens }: { itens: RespostaPainel["atencao"] }): JSX.Element {
  return (
    <Cartao
      titulo="Quem precisa de você"
      dica="Decisões que o motor não pode tomar sozinho"
      semCorpo
    >
      {itens.length === 0 ? (
        <Vazio>
          Nada pendente de decisão humana. Nenhum número fora do padrão.
        </Vazio>
      ) : (
        <ul className="list-none m-0 p-0">
          {itens.map((i, idx) => {
            const secao = secaoDaRota(i.rota);
            return (
              <li
                key={`${i.tipo}-${i.alvoId ?? idx}`}
                className="border-t border-linha first:border-t-0 px-5 py-3.5 flex items-start gap-3 flex-wrap"
              >
                <div className="shrink-0 pt-0.5">
                  <Selo tom={i.gravidade}>{GRAVIDADE[i.gravidade]}</Selo>
                </div>
                <div className="flex-1 min-w-[220px]">
                  <div className="text-[13px] font-medium">{i.titulo}</div>
                  <p className="text-[12.5px] text-tinta-2 mt-0.5 leading-snug m-0">
                    {i.detalhe}
                  </p>
                </div>
                <div className="shrink-0">
                  <Botao
                    pequeno
                    onClick={() => {
                      if (i.alvoId) passar(secao, { numeroId: i.alvoId });
                      irPara(secao);
                    }}
                  >
                    Resolver
                  </Botao>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <div className="px-5 py-3 border-t border-linha">
        <Rotulo>
          Ordem por gravidade, não por data — o mais antigo pode não ser o mais
          urgente
        </Rotulo>
      </div>
    </Cartao>
  );
}
